import {
  UserModel,
  UserPrivateDataModel,
  UserPublicDataModel,
  UserAddressModel,
  UserMetaDataModel,
} from '@loavies/c2c-models'

import { mapImage } from './image-mappers'

/**
 * @param {Array<Object>} users
 * @param {boolean} [ignorePrivateData]
 * @return {loavies.models.user.UserModel[]}
 */
export function mapUsers(users, ignorePrivateData) {
  return users.map(user => mapUser(user, ignorePrivateData))
}

/**
 * @param {Object} user
 * @param {boolean} [ignorePrivateData]
 * @returns {loavies.models.user.UserModel}
 */
export function mapUser(user, ignorePrivateData = false) {
  return new UserModel({
    id: user.id.uuid,
    email: user.attributes.email,
    firstName: user.attributes.profile.firstName,
    lastName: user.attributes.profile.lastName,
    displayName: user.attributes.profile.displayName === 'null' ? null : user.attributes.profile.displayName,
    abbreviatedName: user.attributes.profile.abbreviatedName,
    bio: user.attributes.profile.bio,
    image: user.profileImage ? mapImage(user.profileImage) : null,
    createdAt: user.attributes.createdAt,
    banned: user.attributes.banned,
    deleted: user.attributes.deleted,
    privateData: user.attributes.profile.privateData && !ignorePrivateData ? mapPrivateData(user.attributes.profile.privateData) : null,
    publicData: user.attributes.profile.publicData ? mapPublicData(user.attributes.profile.publicData) : null,
    metaData: user.attributes.profile.metadata ? mapMetaData(user.attributes.profile.metadata) : null,
  })
}

/**
 * @param {Object} privateData
 * @return {loavies.models.user.UserPrivateDataModel}
 */
function mapPrivateData(privateData) {
  return new UserPrivateDataModel({
    wishlist: privateData?.wishlist,
    chatBrowserNotificationsEnabled: privateData?.chatBrowserNotificationsEnabled,
    accountOnboardingEnabled: privateData?.accountOnboardingEnabled,
    generalOnboardingEnabled: privateData?.generalOnboardingEnabled,
    lastEventSequenceIdNotificationShown: privateData?.lastEventSequenceIdNotificationShown,
    lastEventSequenceIdMessageRead: privateData?.lastEventSequenceIdMessageRead,
  })
}

/**
 * @param {Object} publicData
 * @return {loavies.models.user.UserPublicDataModel}
 */
function mapPublicData(publicData) {
  return new UserPublicDataModel({
    storeName: publicData?.storeName,
    storeIsClosed: publicData?.storeIsClosed,
    storeGridType: publicData?.storeGridType,
    amountOfStoreViews: publicData?.amountOfStoreViews,
    amountOfFollowers: publicData?.amountOfFollowers,
    followingUsers: publicData?.followingUsers,
    banner: publicData?.banner,
    userLabels: publicData?.userLabels,
  })
}

/**
 * For backwards compatability we check multiple values
 * @param {Object} address
 * @return {loavies.models.user.UserAddressModel}
 */
export function mapUserAddress(address) {
  return new UserAddressModel({
    id: address.id,
    firstName: address.firstName,
    lastName: address.lastName,
    countryId: address.countryId || address.countryCode,
    postcode: address.postcode || address.postalCode,
    city: address.city,
    street: address.street,
    number: address.number,
    addition: address.addition,
    additionalAddressLine: address.details || address.additionalAddressLine,
    defaultShipping: address.defaultShipping || address.isDefaultShippingAddress,
    isBusinessAddress: address.isBusinessAddress,
  })
}

/**
 * @param {Object} metaData
 * @return {loavies.models.user.UserMetaDataModel}
 */
function mapMetaData(metaData) {
  return new UserMetaDataModel({
    userId: metaData.userId,
    magentoId: metaData.magentoId,
    storeCode: metaData.storeCode,
  })
}
